import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { ClockIcon, LightbulbIcon, MessageCircleIcon, HomeIcon, AnchorIcon, ShieldIcon, StarIcon, ArrowRightIcon, PhoneIcon, CheckCircleIcon, AlertTriangleIcon } from 'lucide-react';
import { FadeIn } from '../components/FadeIn';
import { TrustBar } from '../components/TrustBar';
import { SectionHeading } from '../components/SectionHeading';
import { Button } from '../components/Button';
import { CTASection } from '../components/CTASection';
interface HomePageProps {
  onNavigate: (page: string) => void;
}
export function HomePage({
  onNavigate
}: HomePageProps) {
  return <div className="w-full overflow-hidden" data-id="element-387">
      {/* Hero Section */}
      <section className="relative pt-32 md:pt-44 pb-12 md:pb-24 bg-white" data-id="element-388">
        <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-389">
          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12" data-id="element-390">
            <div className="w-full md:w-1/2 z-10" data-id="element-391">
              {/* Mobile avatar — visible only on small screens */}
              <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.5
            }} className="md:hidden mb-5" data-id="element-392">
                <img src="https://cdn.magicpatterns.com/uploads/8YqgEQJ5jSY63NDsDuuJzL/5A2A7661-bewerkt-3.jpg" alt="Jordy de Gier" className="w-20 h-20 rounded-full object-cover object-top shadow-card-lg border-2 border-gold/30" data-id="element-393" />
              </motion.div>

              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }} className="text-3xl md:text-5xl lg:text-6xl font-extrabold text-navy leading-tight mb-4 md:mb-6 tracking-tight" data-id="element-394">
                Hoi, ik ben Jordy.
                <br data-id="element-395" />
                <span className="text-xl md:text-3xl lg:text-4xl font-bold text-gold mt-1 md:mt-2 block" data-id="element-396">
                  Jouw persoonlijke financieel adviseur.
                </span>
              </motion.h1>
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="text-base md:text-xl text-gray-600 mb-6 md:mb-8 leading-relaxed max-w-lg" data-id="element-397">
                Hypotheekadvies en verzekeringen in Badhoevedorp, Amsterdam en
                Hoofddorp. Ik zoek net zolang door tot we de beste oplossing
                voor jouw situatie hebben gevonden.
              </motion.p>

              {/* Trust signal — prominent on mobile */}
              <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.15
            }} className="flex items-center gap-2 mb-5 md:hidden bg-warm px-4 py-2.5 rounded-brand border border-gray-100" data-id="element-398">
                <CheckCircleIcon className="w-5 h-5 text-gold flex-shrink-0" data-id="element-399" />
                <span className="text-sm text-navy font-semibold" data-id="element-400">
                  Eerste gesprek is altijd gratis en vrijblijvend
                </span>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="flex flex-col sm:flex-row gap-3 md:gap-4" data-id="element-401">
                <Button variant="primary" size="lg" onClick={() => onNavigate('contact')} className="!py-3.5 md:!py-4" data-id="element-402">
                  Plan een gratis kennismaking
                </Button>
                <Button variant="secondary" size="lg" href="tel:0232052161" icon={<PhoneIcon className="w-5 h-5" data-id="element-404" />} iconPosition="left" className="!py-3.5 md:!py-4" data-id="element-403">
                  Bel direct
                </Button>
              </motion.div>

              {/* Trust signal — desktop only */}
              <motion.p initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.4
            }} className="hidden md:flex text-sm text-gray-500 mt-6 items-center gap-2 font-medium" data-id="element-405">
                <CheckCircleIcon className="w-4 h-4 text-gold" data-id="element-406" />
                Eerste gesprek is altijd gratis en vrijblijvend
              </motion.p>
            </div>

            {/* Hero image — large on desktop, compact on mobile */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="w-full md:w-1/2 relative h-[280px] md:h-[600px] rounded-2xl overflow-hidden shadow-card-lg" data-id="element-407">
              <img src="https://cdn.magicpatterns.com/uploads/8YqgEQJ5jSY63NDsDuuJzL/5A2A7661-bewerkt-3.jpg" alt="Jordy de Gier — Financieel Adviseur" className="absolute inset-0 w-full h-full object-cover object-top" data-id="element-408" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/60 via-transparent to-transparent" data-id="element-409" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Indicators Bar */}
      <TrustBar data-id="element-410" />

      {/* Personal Intro Section */}
      <section className="py-20 md:py-28 bg-warm" data-id="element-411">
        <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-412">
          <div className="flex flex-col md:flex-row items-center gap-12" data-id="element-413">
            <FadeIn className="w-full md:w-1/3" data-id="element-414">
              <img src="https://cdn.magicpatterns.com/uploads/8YqgEQJ5jSY63NDsDuuJzL/5A2A7661-bewerkt-3.jpg" alt="Jordy de Gier" loading="lazy" className="rounded-2xl shadow-card-lg aspect-square object-cover object-top w-full max-w-sm mx-auto" data-id="element-415" />
            </FadeIn>
            <FadeIn delay={0.2} className="w-full md:w-2/3" data-id="element-416">
              <h4 className="text-gold font-bold tracking-wider uppercase mb-2" data-id="element-417">
                Over Jordy
              </h4>
              <h2 className="text-3xl md:text-4xl font-bold text-navy mb-6" data-id="element-418">
                Ik kijk verder dan de standaard regeltjes
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6" data-id="element-419">
                Een hypotheek of verzekering is geen formuliertje — het gaat om
                de belangrijkste beslissingen in je leven. Daarom krijg je bij
                mij geen keuzemenu of wisselende contactpersonen. Je hebt mijn
                mobiele nummer, ik reageer via WhatsApp, en als een bank 'nee'
                zegt, zoek ik net zolang door tot we wél een oplossing vinden.
              </p>
              <ul className="space-y-3 mb-8" data-id="element-420">
                <li className="flex items-center text-navy font-medium" data-id="element-421">
                  <CheckCircleIcon className="w-5 h-5 text-gold mr-3" data-id="element-422" /> 15+
                  jaar ervaring in de financiële sector
                </li>
                <li className="flex items-center text-navy font-medium" data-id="element-423">
                  <CheckCircleIcon className="w-5 h-5 text-gold mr-3" data-id="element-424" /> Erkend
                  Financieel Adviseur (SEH)
                </li>
                <li className="flex items-center text-navy font-medium" data-id="element-425">
                  <CheckCircleIcon className="w-5 h-5 text-gold mr-3" data-id="element-426" />{' '}
                  Bereikbaar via WhatsApp, ook 's avonds
                </li>
              </ul>

              {/* Lokaal geworteld element */}
              <button onClick={() => onNavigate('contact')} className="flex items-center gap-4 bg-white p-4 rounded-xl border border-gray-100 shadow-sm hover:shadow-md hover:border-gold/30 transition-all duration-300 cursor-pointer w-full text-left group" data-id="element-427">
                <img src="https://cdn.magicpatterns.com/uploads/kirqiWzVxRbHNevkp7sjEZ/JDG_Nieuwsbrief_heading_goed_zonder_(1).jpg" alt="JDG Kantoor aan de Sloterweg, Badhoevedorp" className="w-20 h-14 md:w-24 md:h-16 object-cover rounded-lg flex-shrink-0" data-id="element-428" />
                <div data-id="element-429">
                  <p className="text-navy font-bold text-sm group-hover:text-gold transition-colors" data-id="element-430">
                    Gevestigd aan de Sloterweg in Badhoevedorp
                  </p>
                  <p className="text-gray-500 text-xs mt-0.5" data-id="element-431">
                    Persoonlijk advies, dicht bij huis
                  </p>
                </div>
                <ArrowRightIcon className="w-5 h-5 text-gray-300 group-hover:text-gold transition-colors ml-auto flex-shrink-0" data-id="element-432" />
              </button>

              <div className="mt-6" data-id="element-433">
                <Button variant="ghost" onClick={() => onNavigate('over-ons')} data-id="element-434">
                  Lees meer over mij
                </Button>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* USP Section */}
      <section className="py-20 md:py-28 bg-white" data-id="element-435">
        <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-436">
          <SectionHeading title="Waarom klanten bij mij terugkomen" subtitle="Veel van mijn klanten bellen me jaren later nog steeds. Niet omdat het moet, maar omdat ze weten dat ik meedenk." data-id="element-437" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10" data-id="element-438">
            <FadeIn delay={0.1} className="bg-warm p-8 rounded-2xl shadow-card border border-gray-100" data-id="element-439">
              <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center mb-6 shadow-sm" data-id="element-440">
                <ClockIcon className="w-7 h-7 text-gold" data-id="element-441" />
              </div>
              <h3 className="text-2xl font-bold text-navy mb-3" data-id="element-442">
                Snel Schakelen
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-443">
                Geen lange wachttijden. Een afspraak is vaak dezelfde dag nog
                mogelijk, zodat je direct weet waar je aan toe bent.
              </p>
            </FadeIn>

            <FadeIn delay={0.2} className="bg-warm p-8 rounded-2xl shadow-card border border-gray-100" data-id="element-444">
              <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center mb-6 shadow-sm" data-id="element-445">
                <LightbulbIcon className="w-7 h-7 text-gold" data-id="element-446" />
              </div>
              <h3 className="text-2xl font-bold text-navy mb-3" data-id="element-447">
                Creatieve Oplossingen
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-448">
                Waar standaard banken 'nee' zeggen, zoek ik net zolang door tot
                we toch een passende oplossing vinden.
              </p>
            </FadeIn>

            <FadeIn delay={0.3} className="bg-warm p-8 rounded-2xl shadow-card border border-gray-100" data-id="element-449">
              <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center mb-6 shadow-sm" data-id="element-450">
                <MessageCircleIcon className="w-7 h-7 text-gold" data-id="element-451" />
              </div>
              <h3 className="text-2xl font-bold text-navy mb-3" data-id="element-452">
                Persoonlijk Contact
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-453">
                Korte lijntjes en direct contact via WhatsApp met je eigen
                adviseur. Geen keuzemenu's of wisselende contactpersonen.
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Core Services */}
      <section className="py-20 md:py-28 bg-warm" data-id="element-454">
        <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-455">
          <SectionHeading title="Waar ik je mee help" subtitle="Van je eerste koophuis tot een woonboot op de Amstel. En als er iets misgaat, bel je mij." data-id="element-456" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-457">
            {/* Pillar 1: Hypotheken — Hoofdproduct, visueel prominent */}
            <FadeIn delay={0.1} className="flex flex-col bg-navy rounded-2xl p-8 shadow-card hover:shadow-card-hover transition-all duration-300 relative overflow-hidden" data-id="element-458">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-bl-full" data-id="element-459" />
              <HomeIcon className="w-12 h-12 text-gold mb-6 relative z-10" strokeWidth={1.5} data-id="element-460" />
              <h3 className="text-2xl font-bold text-white mb-4 relative z-10" data-id="element-461">
                Hypotheken
              </h3>
              <p className="text-gray-300 mb-8 flex-grow relative z-10" data-id="element-462">
                Voor starters, doorstromers, ondernemers en bij scheidingen. Ik
                vergelijk de voorwaarden van meer dan 30 geldverstrekkers en
                zoek de beste deal voor jouw situatie.
              </p>
              <div className="flex flex-col gap-3 relative z-10 mt-auto" data-id="element-463">
                <Button variant="primary" onClick={() => onNavigate('contact')} className="w-full" data-id="element-464">
                  Plan een gesprek
                </Button>
                <button onClick={() => onNavigate('hypotheken')} className="text-white hover:text-gold font-medium transition-colors text-center py-2" data-id="element-465">
                  Meer over hypotheken
                </button>
              </div>
            </FadeIn>

            {/* Pillar 2: Woonboten */}
            <FadeIn delay={0.2} className="flex flex-col bg-white rounded-2xl p-8 border border-gray-100 shadow-card hover:shadow-card-hover transition-all duration-300 group" data-id="element-466">
              <AnchorIcon className="w-12 h-12 text-navy mb-6" strokeWidth={1.5} data-id="element-467" />
              <h3 className="text-2xl font-bold text-navy mb-4" data-id="element-468">Woonboten</h3>
              <p className="text-gray-600 mb-8 flex-grow" data-id="element-469">
                De meeste banken zeggen 'nee' tegen woonboten. Ik heb het
                netwerk van gespecialiseerde geldverstrekkers die wél
                financieren. Van ligplaatsvergunning tot notaris — ik begeleid
                je bij elke stap.
              </p>
              <button onClick={() => onNavigate('woonboten')} className="flex items-center text-gold font-bold group-hover:text-navy transition-colors" data-id="element-470">
                Lees meer{' '}
                <ArrowRightIcon className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" data-id="element-471" />
              </button>
            </FadeIn>

            {/* Pillar 3: Verzekeringen */}
            <FadeIn delay={0.3} className="flex flex-col bg-white rounded-2xl p-8 border border-gray-100 shadow-card hover:shadow-card-hover transition-all duration-300 group" data-id="element-472">
              <ShieldIcon className="w-12 h-12 text-navy mb-6" strokeWidth={1.5} data-id="element-473" />
              <h3 className="text-2xl font-bold text-navy mb-4" data-id="element-474">
                Verzekeringen
              </h3>
              <p className="text-gray-600 mb-8 flex-grow" data-id="element-475">
                Wat als er iets gebeurt? Dan bel je mij. Ik regel de
                schadeafhandeling en zorg dat je goed verzekerd bent — niet
                meer, niet minder.
              </p>
              <div className="flex flex-col gap-3 mt-auto" data-id="element-476">
                <Button variant="secondary" href="tel:0232052161" className="w-full" data-id="element-477">
                  Schade melden
                </Button>
                <button onClick={() => onNavigate('verzekeringen')} className="flex items-center justify-center text-gold font-bold group-hover:text-navy transition-colors" data-id="element-478">
                  Bekijk verzekeringen{' '}
                  <ArrowRightIcon className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" data-id="element-479" />
                </button>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Schade Melden Compact Banner */}
      <section className="bg-navy py-6 md:py-8 border-t-4 border-gold" data-id="element-480">
        <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-481">
          <FadeIn className="flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-6" data-id="element-482">
            <div className="flex items-center gap-4 text-center sm:text-left" data-id="element-483">
              <div className="w-12 h-12 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 hidden sm:flex" data-id="element-484">
                <AlertTriangleIcon className="w-6 h-6 text-gold" data-id="element-485" />
              </div>
              <div data-id="element-486">
                <h3 className="text-xl md:text-2xl font-extrabold text-gold" data-id="element-487">
                  Schade? Bel mij direct.
                </h3>
                <p className="text-gray-300 text-sm mt-0.5" data-id="element-488">
                  Geen wachttijden. Ik regel de afhandeling persoonlijk.
                </p>
              </div>
            </div>
            <Button variant="primary" href="tel:0232052161" icon={<PhoneIcon className="w-5 h-5" data-id="element-490" />} iconPosition="left" className="w-full sm:w-auto flex-shrink-0" data-id="element-489">
              Bel 023-205 2161
            </Button>
          </FadeIn>
        </div>
      </section>

      {/* Trust / Reviews Section */}
      <section className="py-20 md:py-28 bg-white relative overflow-hidden" data-id="element-491">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[400px] text-navy/5 font-serif leading-none select-none pointer-events-none" data-id="element-492">
          "
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10" data-id="element-493">
          <SectionHeading title="Wat klanten over mij zeggen" data-id="element-494" />

          <div className="flex overflow-x-auto md:grid md:grid-cols-2 gap-4 md:gap-6 pb-8 md:pb-0 hide-scrollbar snap-x snap-mandatory -mx-4 px-4 md:mx-0 md:px-0" data-id="element-495">
            <div className="min-w-[85vw] sm:min-w-[340px] md:min-w-0 bg-warm p-6 md:p-8 rounded-2xl shadow-card snap-center flex-shrink-0 border border-gray-100" data-id="element-496">
              <div className="flex gap-1 mb-4" data-id="element-497">
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-498" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-499" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-500" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-501" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-502" />
              </div>
              <p className="text-gray-700 italic mb-6 leading-relaxed" data-id="element-503">
                "Jordy heeft ons fantastisch geholpen bij onze eerste woning.
                Snel, duidelijk en altijd bereikbaar via WhatsApp. Geen vraag
                was te veel."
              </p>
              <p className="font-bold text-navy" data-id="element-504">Sander & Lisa</p>
              <p className="text-sm text-gray-500" data-id="element-505">Starters</p>
            </div>

            <div className="min-w-[85vw] sm:min-w-[340px] md:min-w-0 bg-warm p-6 md:p-8 rounded-2xl shadow-card snap-center flex-shrink-0 border border-gray-100" data-id="element-506">
              <div className="flex gap-1 mb-4" data-id="element-507">
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-508" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-509" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-510" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-511" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-512" />
              </div>
              <p className="text-gray-700 italic mb-6 leading-relaxed" data-id="element-513">
                "Als ZZP'er dacht ik dat een hypotheek lastig zou worden. Jordy
                dacht out-of-the-box en had binnen een week een akkoord bij een
                bank."
              </p>
              <p className="font-bold text-navy" data-id="element-514">Mark</p>
              <p className="text-sm text-gray-500" data-id="element-515">Ondernemer</p>
            </div>

            <div className="min-w-[85vw] sm:min-w-[340px] md:min-w-0 bg-warm p-6 md:p-8 rounded-2xl shadow-card snap-center flex-shrink-0 border border-gray-100" data-id="element-516">
              <div className="flex gap-1 mb-4" data-id="element-517">
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-518" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-519" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-520" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-521" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-522" />
              </div>
              <p className="text-gray-700 italic mb-6 leading-relaxed" data-id="element-523">
                "Voor de financiering van onze woonboot kregen we overal 'nee'
                te horen, totdat we bij Jordy kwamen. Zeer deskundig in deze
                niche!"
              </p>
              <p className="font-bold text-navy" data-id="element-524">Familie de Vries</p>
              <p className="text-sm text-gray-500" data-id="element-525">Woonboot</p>
            </div>

            <div className="min-w-[85vw] sm:min-w-[340px] md:min-w-0 bg-warm p-6 md:p-8 rounded-2xl shadow-card snap-center flex-shrink-0 border border-gray-100" data-id="element-526">
              <div className="flex gap-1 mb-4" data-id="element-527">
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-528" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-529" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-530" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-531" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-532" />
              </div>
              <p className="text-gray-700 italic mb-6 leading-relaxed" data-id="element-533">
                "Na mijn scheiding zat ik met veel onzekerheid. Jordy bracht
                rust en overzicht, en zorgde dat ik in het huis kon blijven
                wonen."
              </p>
              <p className="font-bold text-navy" data-id="element-534">Emma</p>
              <p className="text-sm text-gray-500" data-id="element-535">Scheiding</p>
            </div>
          </div>

          <FadeIn delay={0.2} className="text-center mt-12" data-id="element-536">
            <a href="#" className="inline-flex items-center gap-3 text-navy hover:text-gold font-bold transition-colors bg-white px-8 py-4 rounded-full shadow-md border border-gray-100 hover:border-gold" data-id="element-537">
              <img src="https://cdn.magicpatterns.com/uploads/6V3PK5rxtr5xmZxaw5G5yP/advieskeuze_lr.png" alt="Advieskeuze" className="h-6 object-contain" data-id="element-538" />
              Bekijk alle reviews op Advieskeuze (9.8/10)
            </a>
          </FadeIn>
        </div>
      </section>

      {/* Final CTA */}
      <CTASection title="Benieuwd wat ik voor je kan betekenen?" subtitle="Plan een vrijblijvend kennismakingsgesprek — kost niks, levert altijd wat op." buttonText="Plan een gratis kennismaking" onNavigate={onNavigate} page="contact" variant="dark" data-id="element-539" />
    </div>;
}